// Pure date and math helpers for the health and finance calculators.
// Everything here is deterministic and runs locally in the browser: no
// network, no external dependencies. Date parsing uses local Y/M/D parts to
// avoid timezone drift from `new Date('yyyy-mm-dd')` (which is UTC-based).

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse a `yyyy-mm-dd` string into a local-midnight Date, or null. */
export function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Format a Date as a `yyyy-mm-dd` string (local parts). */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Add a whole number of days to a Date, returning a new Date. */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/** Whole days between two dates (b - a), based on local midnight. */
export function daysBetween(a: Date, b: Date): number {
  const startA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const startB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((startB - startA) / MS_PER_DAY);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Human friendly date, e.g. "Mon, Jul 4, 2026". */
export function formatLongDate(date: Date): string {
  return `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function clampInt(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/* --------------------------------------------------------------------------
 * Pregnancy (Naegele's rule)
 * ----------------------------------------------------------------------- */

export type PregnancyMilestone = { label: string; date: string; detail: string };

export type PregnancyResult = {
  ok: boolean;
  message?: string;
  dueDate?: string;
  gestationalAge?: string;
  gestationalWeeks?: number;
  trimester?: string;
  progress?: string;
  conception?: string;
  milestones?: PregnancyMilestone[];
};

export function computePregnancy(
  lmp: string,
  cycleLengthRaw: string,
  today: Date,
): PregnancyResult {
  const lmpDate = parseDateInput(lmp);
  if (!lmpDate) {
    return { ok: false, message: 'Enter the first day of your last menstrual period.' };
  }
  const cycleLength = clampInt(Number(cycleLengthRaw), 20, 45, 28);
  // Naegele's rule with a cycle-length adjustment: due date is 280 days from
  // the LMP for a 28-day cycle; shift by (cycle - 28) days otherwise.
  const dueDate = addDays(lmpDate, 280 + (cycleLength - 28));
  const conception = addDays(lmpDate, cycleLength - 14);

  const ageDays = daysBetween(lmpDate, today);
  if (ageDays < 0) {
    return {
      ok: false,
      message: 'The last period date is in the future. Check the date.',
    };
  }

  const weeks = Math.floor(ageDays / 7);
  const days = ageDays % 7;
  let trimester = 'First trimester';
  if (weeks >= 28) trimester = 'Third trimester';
  else if (weeks >= 14) trimester = 'Second trimester';
  const progressPct = Math.min(100, Math.round((ageDays / 280) * 100));

  const milestones: PregnancyMilestone[] = [
    {
      label: 'Estimated conception',
      date: formatLongDate(conception),
      detail: 'Around ovulation',
    },
    {
      label: 'End of first trimester',
      date: formatLongDate(addDays(lmpDate, 13 * 7 + 6)),
      detail: 'Week 13',
    },
    {
      label: 'End of second trimester',
      date: formatLongDate(addDays(lmpDate, 27 * 7 + 6)),
      detail: 'Week 27',
    },
    {
      label: 'Viability milestone',
      date: formatLongDate(addDays(lmpDate, 24 * 7)),
      detail: 'Week 24',
    },
    {
      label: 'Full term begins',
      date: formatLongDate(addDays(lmpDate, 37 * 7)),
      detail: 'Week 37',
    },
    {
      label: 'Estimated due date',
      date: formatLongDate(dueDate),
      detail: 'Week 40',
    },
  ];

  return {
    ok: true,
    dueDate: formatLongDate(dueDate),
    gestationalAge: `${weeks} weeks, ${days} ${days === 1 ? 'day' : 'days'}`,
    gestationalWeeks: weeks,
    trimester,
    progress: `${progressPct}% of ~40 weeks`,
    conception: formatLongDate(conception),
    milestones,
  };
}

/* --------------------------------------------------------------------------
 * Ovulation / fertile window
 * ----------------------------------------------------------------------- */

export type OvulationResult = {
  ok: boolean;
  message?: string;
  ovulationDate?: string;
  fertileStart?: string;
  fertileEnd?: string;
  nextPeriod?: string;
  fertileWindow?: string;
};

export function computeOvulation(lmp: string, cycleLengthRaw: string): OvulationResult {
  const lmpDate = parseDateInput(lmp);
  if (!lmpDate) {
    return { ok: false, message: 'Enter the first day of your last period.' };
  }
  const cycleLength = clampInt(Number(cycleLengthRaw), 20, 45, 28);
  // Ovulation is estimated ~14 days before the next period (luteal phase).
  const ovulation = addDays(lmpDate, cycleLength - 14);
  const fertileStart = addDays(ovulation, -5);
  const fertileEnd = addDays(ovulation, 1);
  const nextPeriod = addDays(lmpDate, cycleLength);

  return {
    ok: true,
    ovulationDate: formatLongDate(ovulation),
    fertileStart: formatLongDate(fertileStart),
    fertileEnd: formatLongDate(fertileEnd),
    nextPeriod: formatLongDate(nextPeriod),
    fertileWindow: `${formatLongDate(fertileStart)} – ${formatLongDate(fertileEnd)}`,
  };
}

/* --------------------------------------------------------------------------
 * Sleep cycles (90-minute cycles + fall-asleep buffer)
 * ----------------------------------------------------------------------- */

const CYCLE_MINUTES = 90;
const FALL_ASLEEP_MINUTES = 15;

export type SleepSuggestion = { time: string; cycles: number; hours: string; recommended: boolean };

/** Parse a `HH:MM` 24h string into minutes since midnight, or null. */
export function parseTimeInput(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Format minutes-since-midnight (mod 24h) as a 12-hour clock, e.g. "6:30 AM". */
export function formatClock(totalMinutes: number): string {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hours24 = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

function cyclesToHours(cycles: number): string {
  const hours = (cycles * CYCLE_MINUTES) / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

/**
 * Suggest times based on 90-minute sleep cycles. `direction === 'bedtime'`
 * takes a wake time and returns bedtimes; `'wake'` takes a bedtime and returns
 * wake times. A 15-minute buffer to fall asleep is always included.
 */
export function computeSleep(
  time: string,
  direction: 'bedtime' | 'wake',
): { ok: boolean; message?: string; suggestions?: SleepSuggestion[] } {
  const minutes = parseTimeInput(time);
  if (minutes === null) {
    return { ok: false, message: 'Enter a valid time in 24-hour HH:MM format.' };
  }
  const cycleCounts = [6, 5, 4, 3];
  const suggestions: SleepSuggestion[] = cycleCounts.map((cycles) => {
    const sleepMinutes = cycles * CYCLE_MINUTES;
    const target =
      direction === 'bedtime'
        ? minutes - sleepMinutes - FALL_ASLEEP_MINUTES
        : minutes + FALL_ASLEEP_MINUTES + sleepMinutes;
    return {
      time: formatClock(target),
      cycles,
      hours: cyclesToHours(cycles),
      recommended: cycles === 5 || cycles === 6,
    };
  });
  return { ok: true, suggestions };
}

/* --------------------------------------------------------------------------
 * Loan amortization schedule
 * ----------------------------------------------------------------------- */

export type AmortizationRow = {
  period: number;
  payment: string;
  principal: string;
  interest: string;
  balance: string;
};

export type AmortizationResult = {
  ok: boolean;
  message?: string;
  monthlyPayment?: string;
  totalPaid?: string;
  totalInterest?: string;
  numberOfPayments?: number;
  schedule?: AmortizationRow[];
};

function money(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function computeAmortization(
  amountRaw: string,
  rateRaw: string,
  yearsRaw: string,
): AmortizationResult {
  const amount = Number(amountRaw);
  const rate = Number(rateRaw);
  const years = Number(yearsRaw);

  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, message: 'Enter a loan amount greater than zero.' };
  }
  if (!Number.isFinite(rate) || rate < 0) {
    return { ok: false, message: 'Enter an interest rate of zero or more.' };
  }
  if (!Number.isFinite(years) || years <= 0) {
    return { ok: false, message: 'Enter a loan term greater than zero.' };
  }

  const months = Math.min(Math.round(years * 12), 1200);
  const monthlyRate = rate / 100 / 12;
  const monthlyPayment =
    monthlyRate === 0
      ? amount / months
      : (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

  const schedule: AmortizationRow[] = [];
  let balance = amount;
  let totalInterest = 0;
  let totalPaid = 0;

  for (let period = 1; period <= months; period += 1) {
    const interest = balance * monthlyRate;
    let principal = monthlyPayment - interest;
    // Final payment: absorb rounding so the balance lands exactly on zero.
    if (period === months || principal > balance) {
      principal = balance;
    }
    const payment = principal + interest;
    balance = Math.max(0, balance - principal);
    totalInterest += interest;
    totalPaid += payment;
    schedule.push({
      period,
      payment: money(payment),
      principal: money(principal),
      interest: money(interest),
      balance: money(balance),
    });
  }

  return {
    ok: true,
    monthlyPayment: money(monthlyPayment),
    totalPaid: money(totalPaid),
    totalInterest: money(totalInterest),
    numberOfPayments: months,
    schedule,
  };
}
